import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../store/hooks';
import { login } from '../store/slices/authSlice';
import axiosInstance from '../config/axiosInstance';

type PageState = 'loading' | 'ready' | 'invalid' | 'success';

const SetPassword: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const token = searchParams.get('token') || '';

    const [pageState, setPageState] = useState<PageState>('loading');
    const [firstName, setFirstName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!token) {
            setPageState('invalid');
            return;
        }

        axiosInstance
            .get(`/auth/verify-email?token=${token}`)
            .then((res) => {
                setFirstName(res.data.firstName);
                setEmail(res.data.email);
                setPageState('ready');
            })
            .catch(() => setPageState('invalid'));
    }, [token]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            const res = await axiosInstance.post('/auth/set-password', { token, password });
            const { accessToken, refreshToken, user } = res.data;

            localStorage.setItem('auth', JSON.stringify({
                user,
                token: accessToken,
                refreshToken,
            }));

            dispatch(login({
                user: {
                    id: user.id,
                    email: user.email,
                    name: `${user.firstName} ${user.lastName}`,
                    role: user.role,
                    avatar: user.profileImage || undefined,
                },
                token: accessToken,
            }));

            setPageState('success');
            setTimeout(() => navigate('/'), 2000);
        } catch (err: any) {
            const msg = err.response?.data?.message;
            setError(typeof msg === 'string' ? msg : 'Something went wrong. The link may have already been used.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-background min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0 opacity-5" style={{
                backgroundColor: '#1e293b',
                backgroundImage: 'radial-gradient(#334155 1px, transparent 1px)',
                backgroundSize: '20px 20px',
            }} />
            <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />

            <div className="relative z-10 w-full max-w-6xl grid lg:grid-cols-2 gap-0 bg-white rounded-3xl shadow-2xl overflow-hidden">

                {/* Left panel */}
                <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10" style={{
                        backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
                        backgroundSize: '20px 20px',
                    }} />
                    <div className="absolute top-20 right-20 w-64 h-64 bg-primary/20 rounded-full blur-3xl" />
                    <div className="absolute bottom-20 left-20 w-64 h-64 bg-accent/20 rounded-full blur-3xl" />

                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-8">
                            <img src="/logo.png" alt="Enjoy Triathlon" className="w-44 h-auto object-contain" />
                        </div>
                        <h2 className="text-4xl font-bold text-white mb-4 leading-tight">
                            Welcome to<br />Enjoy Triathlon
                        </h2>
                        <p className="text-slate-300 text-lg font-medium leading-relaxed">
                            Set your password to activate your account and start training.
                        </p>
                    </div>

                    <div className="relative z-10 space-y-6">
                        {[
                            { icon: 'fa-chart-line', color: 'primary', title: 'Track Progress', desc: 'Monitor every session and see yourself improve.' },
                            { icon: 'fa-dumbbell', color: 'accent', title: 'Custom Training', desc: 'Plans built by your coach just for you.' },
                            { icon: 'fa-trophy', color: 'highlight', title: 'Race Ready', desc: 'Peak on race day with structured preparation.' },
                        ].map((f) => (
                            <div key={f.title} className="flex items-start gap-4">
                                <div className={`w-12 h-12 rounded-xl bg-${f.color}/20 flex items-center justify-center text-${f.color} flex-shrink-0`}>
                                    <i className={`fa-solid ${f.icon} text-xl`}></i>
                                </div>
                                <div>
                                    <h4 className="text-white font-bold mb-1">{f.title}</h4>
                                    <p className="text-slate-400 text-sm">{f.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="absolute bottom-0 right-0 opacity-5">
                        <i className="fa-solid fa-person-biking text-[300px] text-white"></i>
                    </div>
                </div>

                {/* Right panel */}
                <div className="p-8 lg:p-12 flex flex-col justify-center">

                    {/* Mobile logo */}
                    <div className="lg:hidden mb-8 text-center">
                        <img src="/logo.png" alt="Enjoy Triathlon" className="w-32 h-auto object-contain mx-auto" />
                    </div>

                    {/* LOADING */}
                    {pageState === 'loading' && (
                        <div className="flex flex-col items-center justify-center py-16 gap-4">
                            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                            <p className="text-slate-500 font-medium">Verifying your link…</p>
                        </div>
                    )}

                    {/* INVALID */}
                    {pageState === 'invalid' && (
                        <div className="text-center py-16">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <i className="fa-solid fa-link-slash text-red-500 text-2xl"></i>
                            </div>
                            <h2 className="text-2xl font-bold text-slate-800 mb-3">Link invalid or expired</h2>
                            <p className="text-slate-500 mb-8">
                                This link has already been used or has expired.<br />
                                Please contact your coach to get your password.
                            </p>
                            <div className="flex flex-col gap-3">
                                <button
                                    onClick={() => navigate('/login')}
                                    className="text-slate-500 text-sm font-medium hover:underline"
                                >
                                    Back to login
                                </button>
                            </div>
                        </div>
                    )}

                    {/* SUCCESS */}
                    {pageState === 'success' && (
                        <div className="text-center py-16">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <i className="fa-solid fa-circle-check text-green-500 text-2xl"></i>
                            </div>
                            <h2 className="text-2xl font-bold text-slate-800 mb-3">Password set!</h2>
                            <p className="text-slate-500">Redirecting you to your training plan…</p>
                        </div>
                    )}

                    {/* READY — set password form */}
                    {pageState === 'ready' && (
                        <>
                            <div className="mb-8">
                                <h1 className="text-3xl font-bold text-slate-800 mb-2">
                                    Welcome, {firstName}!
                                </h1>
                                <p className="text-slate-500 font-medium">
                                    Create a password for <span className="text-primary font-semibold">{email}</span>
                                </p>
                            </div>

                            {error && (
                                <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                                    <div className="flex items-start gap-3">
                                        <i className="fa-solid fa-circle-exclamation text-red-500 text-lg mt-0.5"></i>
                                        <p className="text-sm text-red-700">{error}</p>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">

                                {/* New password */}
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                                        New password <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <i className="fa-solid fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none"></i>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            required
                                            minLength={8}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="At least 8 characters"
                                            className="w-full pl-11 pr-12 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:bg-white focus:border-primary transition-all"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword((v) => !v)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        >
                                            <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-sm`}></i>
                                        </button>
                                    </div>
                                    {/* Strength bar */}
                                    <div className="flex gap-1 mt-1">
                                        {[8, 12, 16].map((threshold) => (
                                            <div
                                                key={threshold}
                                                className={`h-1 flex-1 rounded-full transition-colors ${
                                                    password.length >= threshold
                                                        ? threshold === 8 ? 'bg-red-400' : threshold === 12 ? 'bg-yellow-400' : 'bg-green-400'
                                                        : 'bg-slate-200'
                                                }`}
                                            />
                                        ))}
                                    </div>
                                </div>

                                {/* Confirm password */}
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                                        Confirm password <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <i className="fa-solid fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none"></i>
                                        <input
                                            type={showConfirm ? 'text' : 'password'}
                                            required
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="Repeat your password"
                                            className="w-full pl-11 pr-12 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:bg-white focus:border-primary transition-all"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirm((v) => !v)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        >
                                            <i className={`fa-solid ${showConfirm ? 'fa-eye-slash' : 'fa-eye'} text-sm`}></i>
                                        </button>
                                    </div>
                                    {confirmPassword && password !== confirmPassword && (
                                        <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full py-4 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            Setting password…
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-check"></i>
                                            Activate my account
                                        </>
                                    )}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SetPassword;
